import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { ImageOutlined as ImagePlaceholderIcon } from '@mui/icons-material';
import { resolveMediaPathsStandalone } from '../../../../detail-form/hooks/useResolvedMediaSync';

type AppMediaImageCellProps = {
  mediaPaths: string[];
  textHtml?: string;
  width?: number;
  height?: number;
};

export const AppMediaImageCell = ({
  mediaPaths,
  textHtml,
  width = 72,
  height = 56,
}: AppMediaImageCellProps) => {
  const [resolvedUrls, setResolvedUrls] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setResolvedUrls([]);

    void resolveMediaPathsStandalone(mediaPaths).then((results) => {
      if (cancelled) return;
      setResolvedUrls(
        results.map((result) => result.url).filter((url): url is string => Boolean(url))
      );
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [mediaPaths]);

  if (isLoading) {
    return <Skeleton variant="rounded" width={width} height={height} />;
  }

  if (!resolvedUrls.length) {
    return (
      <Box
        aria-label="Failed to load image preview"
        role="img"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width,
          height,
          borderRadius: 0.5,
          backgroundColor: 'action.hover',
          color: 'error.main',
        }}
      >
        <ImagePlaceholderIcon fontSize="small" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {resolvedUrls.map((url) => (
          <Box
            key={url}
            component="img"
            src={url}
            loading="lazy"
            decoding="async"
            alt=""
            sx={{
              width,
              height,
              borderRadius: 0.5,
              overflow: 'hidden',
              objectFit: 'cover',
              display: 'block',
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
      {textHtml && (
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textAlign: 'left',
            '& p, & div': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: textHtml }}
        />
      )}
    </Box>
  );
};
